import { useRef, useState } from "react";
import dayjs from "dayjs";
import { styled } from "@pigment-css/react";
import { CalendarClockOutline, CalendarOutline, CrossLarge, Trash } from "@acto/web-common/components";
import { useTranslations } from "@acto/web-common/locales";
import type { MeetingCancellationReason } from "@acto/gql/graphql";
import type { Meeting } from "@acto/taskpane/core/domain";
import { useCancelMeetings } from "@acto/taskpane/core/data-access/meetings/useCancelMeetings";
import { useMeetingsAwaitingRecap } from "@acto/taskpane/core/data-access/meetings/useMeetingsAwaitingRecap";
import ActoRoutes from "@acto/taskpane/core/router/routes.config";
import { useDirectionalNavigate, usePageEnterAnimation } from "@acto/taskpane/field-sales-2/utils/viewTransitions";
import { MenuPopover } from "@acto/taskpane/field-sales-2/components/primitives/MenuPopover";
import { TitlePill } from "@acto/taskpane/field-sales-2/components/primitives/TitlePill";
import { TOAST_TRANSITION_DELAY, useToast } from "@acto/taskpane/field-sales-2/components/primitives/Toast";
import { TopNav } from "@acto/taskpane/field-sales-2/components/patterns/TopNav";
import {
  HeaderIconButton,
  HeaderMoreButton,
} from "@acto/taskpane/field-sales-2/components/primitives/HeaderIconButton";
import { AdjustReportsOverlay } from "@acto/taskpane/field-sales-2/components/patterns/recap/AdjustReportsOverlay";
import { RecapList, type RecapRow } from "@acto/taskpane/field-sales-2/components/patterns/recap/RecapList";
import { RecapCreationFlow } from "@acto/taskpane/field-sales-2/components/patterns/recap/RecapCreationFlow";

const Page = styled("main")(({ theme }) => ({
  flex: 1,
  width: "100%",
  display: "flex",
  flexDirection: "column",
  background: theme.fieldSalesTwo.colors.background,
}));

// The content reserves this much bottom padding so the list end can scroll clear of the
// fade instead of being washed out under it.
const BOTTOM_FADE_HEIGHT = 160;

const Content = styled("div")(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[20],
  padding: `0 ${theme.fieldSalesTwo.spacing[16]} ${BOTTOM_FADE_HEIGHT}px`,
  position: "relative",
  zIndex: 1,
}));

// Stands in for the bottom nav's fade on this nav-less page.
const BottomFade = styled("div")(({ theme }) => ({
  position: "fixed",
  left: 0,
  right: 0,
  bottom: 0,
  height: BOTTOM_FADE_HEIGHT,
  background: `linear-gradient(to top, ${theme.fieldSalesTwo.colors.background} 26.5%, rgba(240,238,237,0))`,
  pointerEvents: "none",
  zIndex: 2,
}));

export function OpenRecapsPage() {
  const t = useTranslations("fieldSalesTwo.recaps");
  const tAdjust = useTranslations("fieldSalesTwo.recaps.adjust");
  const navigate = useDirectionalNavigate();
  const contentRef = useRef<HTMLDivElement>(null);
  usePageEnterAnimation(contentRef);

  const handleClose = () => navigate(ActoRoutes.HOME, { direction: "pop" });

  // In-place creation flow (no route): a row opens it for one meeting, the batch button for all.
  const [flowMeetings, setFlowMeetings] = useState<Meeting[] | null>(null);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const { data: openMeetings, refetch: refetchOpenMeetings } = useMeetingsAwaitingRecap();
  const { cancelMeetings, result: cancelResult } = useCancelMeetings();
  const { showToast } = useToast();

  if (flowMeetings) {
    return <RecapCreationFlow meetings={flowMeetings} onClose={() => setFlowMeetings(null)} />;
  }

  // Real open recaps = meetings without a note. Split into today vs earlier visits and map
  // each onto the presentational RecapRow shape (list styling unchanged, content now live).
  // The meetings query carries only a thin client (no street), so the row address is the
  // customer's external reference until a fuller customer fetch is added.
  // scheduledDate is date-only — formatting it as "HH:mm" would fabricate midnight.
  const today = dayjs();
  const toRow = (meeting: Meeting): RecapRow => ({
    id: meeting.id,
    badge:
      meeting.scheduledDate.isSame(today, "day") && meeting.scheduledStartTime
        ? { primary: meeting.scheduledStartTime.slice(0, 5) }
        : { primary: meeting.scheduledDate.format("D"), secondary: meeting.scheduledDate.format("MMM").toUpperCase() },
    name: meeting.customer.name,
    address: meeting.customer.externalReference,
  });

  const meetings = openMeetings ?? [];
  const todayMeetings = meetings.filter((m) => m.scheduledDate.isSame(today, "day"));
  const pastMeetings = meetings
    .filter((m) => m.scheduledDate.isBefore(today, "day"))
    .sort((a, b) => b.scheduledDate.valueOf() - a.scheduledDate.valueOf());

  const sections = [
    { id: "past", heading: t("pastVisits"), icon: <CalendarClockOutline />, meetings: pastMeetings },
    {
      id: "today",
      heading: t("today", { date: today.format("D MMM") }),
      icon: <CalendarOutline />,
      meetings: todayMeetings,
    },
  ].filter((section) => section.meetings.length > 0);

  const handleCancel = async (ids: string[], reason: MeetingCancellationReason, comment?: string) => {
    const cancelled = await cancelMeetings(
      pastMeetings.filter((meeting) => ids.includes(meeting.id)),
      reason,
      comment,
    );
    if (!cancelled) return;

    setAdjustOpen(false);
    refetchOpenMeetings({ requestPolicy: "network-only" });
    showToast({
      message: tAdjust("deleted", { count: cancelled.length }),
      tone: "destructive",
      icon: <Trash aria-hidden="true" />,
      initialDelay: TOAST_TRANSITION_DELAY,
    });
  };

  return (
    <Page>
      <TopNav
        titlePlacement="leading"
        leading={
          <HeaderIconButton type="button" aria-label={t("close")} onClick={handleClose}>
            <CrossLarge width={18} height={18} />
          </HeaderIconButton>
        }
        title={<TitlePill>{t("title")}</TitlePill>}
        trailing={
          pastMeetings.length > 0 ? (
            <MenuPopover
              trigger={<HeaderMoreButton aria-label={tAdjust("moreActions")} />}
              ariaLabel={tAdjust("moreActions")}
              items={[{ label: tAdjust("menuLabel"), onSelect: () => setAdjustOpen(true) }]}
            />
          ) : null
        }
      />

      <Content ref={contentRef}>
        {sections.map((section) => (
          <RecapList
            key={section.id}
            heading={section.heading}
            icon={section.icon}
            rows={section.meetings.map(toRow)}
            ctaLabel={t("completeReports", { count: section.meetings.length })}
            onRowClick={(id) => {
              const meeting = section.meetings.find((m) => m.id === id);
              if (meeting) setFlowMeetings([meeting]);
            }}
            onComplete={() => setFlowMeetings(section.meetings)}
          />
        ))}
      </Content>

      <BottomFade aria-hidden="true" />

      <AdjustReportsOverlay
        isOpen={adjustOpen}
        onOpenChange={setAdjustOpen}
        rows={pastMeetings.map(toRow)}
        onConfirm={handleCancel}
        isSubmitting={cancelResult.fetching}
      />
    </Page>
  );
}
